import { memo } from 'react';
import { css } from '@emotion/css';
import { StaticVerticalStepperContent } from '@frontend/static-vertical-stepper';
import { theme } from '@frontend/theme';
import { Benefit, Deductible, Maximum } from '../../types/insurance-report-api.types';
import { transformRemainingAndTotalValues } from '../../utils/api-helpers';
import { MAX_AND_DED_COLUMNS_CONFIG } from '../../utils/constants';
import { SectionInfoProps } from '../common-components/sectionInfo';
import { MaxAndDeductible } from './components/max-and-deductible';
import { PrintFrequencyAndLimitations } from './components/print-frequency-limitation';
import { PrintHistory } from './components/print-history';
import { PrintPatientPayerDetails } from './components/print-patient-payer-details';
import { PrintProvisions } from './components/print-provisions';
import { PrintableHeader } from './components/printable-header';

type PrintableReportProps = {
  patientsFirstName: string;
  report: SectionInfoProps[];
  printData: SectionInfoProps[];
  maximums: Maximum[];
  deductibles: Deductible[];
  provisions: string[];
  history: StaticVerticalStepperContent[];
  cachedFrequencyLimitationData: Record<string, Record<string, Benefit[]>>;
  favoriteAndRequestedCDTCodes: Record<string, Benefit[]>;
  insuranceStatus: string;
};

export const PrintableReportContainer = memo(
  ({
    patientsFirstName,
    report,
    printData,
    provisions,
    maximums,
    deductibles,
    history,
    cachedFrequencyLimitationData,
    favoriteAndRequestedCDTCodes,
    insuranceStatus,
  }: PrintableReportProps) => {
    return (
      <>
        <PrintableHeader firstName={patientsFirstName} insuranceStatus={insuranceStatus} />
        <PrintPatientPayerDetails data={[...(report || []), ...(printData || [])]} />
        <MaxAndDeductible
          maximum={transformRemainingAndTotalValues(maximums || [])}
          deductible={transformRemainingAndTotalValues(deductibles || [])}
          colConfig={MAX_AND_DED_COLUMNS_CONFIG}
        />
        <div css={[provisionAndHistoryStyles, printStyles]}>
          <PrintProvisions provisions={provisions} />
          <PrintHistory history={history} />
        </div>
        <PrintFrequencyAndLimitations
          frequencyAndLimitations={cachedFrequencyLimitationData}
          favoriteAndRequestedCDTCodes={favoriteAndRequestedCDTCodes}
        />
      </>
    );
  }
);

PrintableReportContainer.displayName = 'PrintableReport';

const provisionAndHistoryStyles = css`
  display: flex;
  flex-direction: row;
  gap: ${theme.spacing(2)};
`;

const printStyles = css`
  @media print {
    @page {
      margin: ${theme.spacing(2)} 0;
    }
  }
`;
